import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { inTextField } from './shortcuts'

const ROW = '[data-list-row]'

/**
 * PRD-018 §3.11 list keys (audit P2-22) for any list whose rows are links carrying `data-list-row` and
 * `data-item-href`: `j` / `k` move focus between rows (across every list on the page, in DOM order),
 * Enter opens the focused row, `x` opens it straight into Mark done, `a` into Reassign. Suppressed in
 * fields and while an overlay is up, like the other single keys. Prior art: Gmail / Linear list keys.
 */
export function useListKeys(enabled = true) {
  const navigate = useNavigate()
  useEffect(() => {
    if (!enabled)
      return
    const rows = () => [...document.querySelectorAll<HTMLElement>(ROW)]
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || inTextField(e.target))
        return
      if (document.querySelector('[role="dialog"], [cmdk-dialog]'))
        return
      const all = rows()
      if (all.length === 0)
        return
      const active = document.activeElement as HTMLElement | null
      const idx = active ? all.indexOf(active.closest(ROW) as HTMLElement) : -1
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault()
        const next = e.key === 'j' ? Math.min(all.length - 1, idx + 1) : Math.max(0, idx < 0 ? 0 : idx - 1)
        all[next]?.focus()
        all[next]?.scrollIntoView({ block: 'nearest' })
        return
      }
      if (idx < 0)
        return
      const row = all[idx]!
      const href = row.dataset.itemHref
      if (!href)
        return
      if (e.key === 'x' && row.dataset.canComplete === 'true') {
        e.preventDefault()
        navigate(withIntent(href, 'complete'))
      }
      else if (e.key === 'a' && row.dataset.canAssign === 'true') {
        e.preventDefault()
        navigate(withIntent(href, 'assign'))
      }
      // Enter is the link's own activation; nothing to add.
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, navigate])
}

function withIntent(href: string, intent: 'complete' | 'assign'): string {
  return `${href}${href.includes('?') ? '&' : '?'}intent=${intent}`
}
